import { useState } from 'react';
import { format } from 'date-fns';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MonthYearPicker } from '@/components/ui/month-year-picker';
import { toast } from 'sonner';
import { timesheetService } from '@/services/timesheets/timesheetService';
import type { Profile } from '@/services/timesheets/types';

interface TimesheetExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profiles: Profile[];
  defaultMonth: Date;
  defaultEmployeeId: string;
}

export const TimesheetExportDialog = ({
  open,
  onOpenChange,
  profiles,
  defaultMonth,
  defaultEmployeeId,
}: TimesheetExportDialogProps) => {
  const [month, setMonth] = useState<Date>(defaultMonth);
  const [employeeId, setEmployeeId] = useState(defaultEmployeeId);
  const [status, setStatus] = useState<string>('approved');
  const [isExporting, setIsExporting] = useState(false);

  // Sync defaults when dialog opens
  const handleOpenChange = (val: boolean) => {
    if (val) {
      setMonth(defaultMonth);
      setEmployeeId(defaultEmployeeId);
      setStatus('approved');
    }
    onOpenChange(val);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const entries = await timesheetService.exportTimesheetEntries({
        month,
        employeeId: employeeId !== 'all' ? employeeId : undefined,
        status: status !== 'all' ? (status as 'pending' | 'approved' | 'rejected') : undefined,
      });

      if (entries.length === 0) {
        toast.info('No entries found matching the selected filters.');
        return;
      }

      // Build CSV
      const headers = ['Employee Name', 'Date', 'Clock In', 'Clock Out', 'Total Hours', 'Status'];

      const rows = entries.map((entry) => {
        const name = entry.profile
          ? `${entry.profile.first_name || ''} ${entry.profile.last_name || ''}`.trim() || 'Unknown'
          : 'Former employee';
        const date = format(new Date(entry.clock_in), 'yyyy-MM-dd');
        const clockIn = format(new Date(entry.clock_in), 'HH:mm');
        const clockOut = entry.clock_out
          ? format(new Date(entry.clock_out), 'HH:mm')
          : '';
        const totalHours = entry.clock_out
          ? ((new Date(entry.clock_out).getTime() - new Date(entry.clock_in).getTime()) / 3_600_000).toFixed(2)
          : 'In Progress';
        return [name, date, clockIn, clockOut, totalHours, entry.status];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const ts = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
      link.download = `team-timesheets-${format(month, 'yyyy-MM')}-${ts}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${entries.length} timesheet entries.`);
      onOpenChange(false);
    } catch (err: any) {
      console.error('Export error:', err);
      toast.error(err.message || 'Failed to export timesheets.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Timesheets
          </DialogTitle>
          <DialogDescription>
            Choose filters and download a CSV of team timesheet data.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {/* Month */}
          <div className="grid gap-1.5">
            <Label>Month</Label>
            <MonthYearPicker
              selectedDate={month}
              onDateChange={setMonth}
              fromYear={2020}
              toYear={2030}
              className="w-full"
            />
          </div>

          {/* Employee */}
          <div className="grid gap-1.5">
            <Label>Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger>
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.first_name} {p.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div className="grid gap-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="all">All statuses</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExporting}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting}>
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting…
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
